import { useEffect, useMemo, useState } from "react";
import {
    Download,
    RefreshCw,
    Bike,
    CheckCircle2,
    Clock3,
    Wifi,
} from "lucide-react";

import { getDeliveryPartners } from "../services/adminApi";

import DeliveryFilters from "../components/delivery/DeliveryFilters";
import DeliveryTable from "../components/delivery/DeliveryTable";

import "./Delivery.css";

function Delivery() {

    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(true);

    const [filters, setFilters] = useState({
        search: "",
        status: "",
        vehicle: "",
        city: "",
        sort: "latest",
    });

    const loadPartners = async () => {

        try {

            setLoading(true);

            const res = await getDeliveryPartners();

            setPartners(res.data.partners || []);

        } catch (error) {

            console.error(error);

        } finally {

            setLoading(false);

        }

    };

    useEffect(() => {

        loadPartners();

    }, []);

    const handleFilterChange = (key, value) => {

        setFilters((prev) => ({
            ...prev,
            [key]: value,
        }));

    };

    const filteredPartners = useMemo(() => {

        let data = [...partners];

        if (filters.search) {

            const search = filters.search.toLowerCase();

            data = data.filter((partner) => {

                const name =
                    partner.userId?.name?.toLowerCase() || "";

                const email =
                    partner.userId?.email?.toLowerCase() || "";

                const phone =
                    partner.userId?.phone?.toLowerCase() || "";

                const vehicle =
                    partner.vehicle?.vehicleNumber?.toLowerCase() || "";

                return (
                    name.includes(search) ||
                    email.includes(search) ||
                    phone.includes(search) ||
                    vehicle.includes(search)
                );

            });

        }

        if (filters.status) {

            data = data.filter(
                (partner) =>
                    partner.work?.status?.toLowerCase() ===
                    filters.status.toLowerCase()
            );

        }

        if (filters.vehicle) {

            data = data.filter(
                (partner) =>
                    partner.vehicle?.vehicleType ===
                    filters.vehicle
            );

        }

        if (filters.city) {

            data = data.filter((partner) =>
                partner.address?.city
                    ?.toLowerCase()
                    .includes(filters.city.toLowerCase())
            );

        }

        switch (filters.sort) {

            case "oldest":
                data.sort(
                    (a, b) =>
                        new Date(a.createdAt) -
                        new Date(b.createdAt)
                );
                break;

            case "az":
                data.sort((a, b) =>
                    (a.userId?.name || "").localeCompare(
                        b.userId?.name || ""
                    )
                );
                break;

            case "za":
                data.sort((a, b) =>
                    (b.userId?.name || "").localeCompare(
                        a.userId?.name || ""
                    )
                );
                break;

            default:
                data.sort(
                    (a, b) =>
                        new Date(b.createdAt) -
                        new Date(a.createdAt)
                );

        }

        return data;

    }, [partners, filters]);

    const totalPartners = partners.length;

    const approvedPartners = partners.filter(
        (partner) =>
            partner.work?.status === "Approved"
    ).length;

    const pendingPartners = partners.filter(
        (partner) =>
            partner.work?.status === "Pending"
    ).length;

    const onlinePartners = partners.filter(
        (partner) =>
            partner.work?.online === true
    ).length;

    return (

        <div className="delivery-page">

            <div className="delivery-header">

                <div>

                    <h1>Delivery Partner Management</h1>

                    <p>
                        Manage all registered delivery partners.
                    </p>

                </div>

                <div className="delivery-actions">

                    <button className="export-btn">

                        <Download size={18} />

                        Export

                    </button>

                    <button
                        className="refresh-btn"
                        onClick={loadPartners}
                    >

                        <RefreshCw size={18} />

                        Refresh

                    </button>

                </div>

            </div>

            <div className="delivery-overview">

                <div className="overview-card">

                    <div className="overview-icon total">

                        <Bike size={24} />

                    </div>

                    <div>

                        <h2>{totalPartners}</h2>

                        <p>Total Partners</p>

                    </div>

                </div>

                <div className="overview-card">

                    <div className="overview-icon approved">

                        <CheckCircle2 size={24} />

                    </div>

                    <div>

                        <h2>{approvedPartners}</h2>

                        <p>Approved</p>

                    </div>

                </div>

                <div className="overview-card">

                    <div className="overview-icon pending">

                        <Clock3 size={24} />

                    </div>

                    <div>

                        <h2>{pendingPartners}</h2>

                        <p>Pending</p>

                    </div>

                </div>

                <div className="overview-card">

                    <div className="overview-icon online">

                        <Wifi size={24} />

                    </div>

                    <div>

                        <h2>{onlinePartners}</h2>

                        <p>Online</p>

                    </div>

                </div>

            </div>

            <DeliveryFilters
                filters={filters}
                onChange={handleFilterChange}
            />

            <div className="delivery-section">

                <DeliveryTable
                    partners={filteredPartners}
                    loading={loading}
                    reload={loadPartners}
                />

            </div>

        </div>

    );

}

export default Delivery;